// <DebugPanel> — collapsible auth-debug panel per DESIGN.md §7.4.
//
// Renders three sections — Identity / Session / id_token claims — as
// key/value tables. Keys use caption font; values use code font.
// Field names are rendered VERBATIM (snake_case, no humanizing).
//
// The panel is a disclosure widget (<details>) for keyboard accessibility
// by default. The caller hands in `onOpen` so the page can lazy-fetch
// /api/debug/whoami only once when the operator opens it.

import { useCallback, useEffect, useRef, useState } from "react";
import "./DebugPanel.css";

export interface DebugSection {
  title: string;
  /**
   * Key/value pairs rendered verbatim. Values are stringified lazily by
   * the component — pass objects/arrays and we'll JSON.stringify them
   * (pretty, 2-space) into a <pre><code> block.
   */
  entries: ReadonlyArray<readonly [string, unknown]>;
}

export interface DebugPanelProps {
  sections?: readonly DebugSection[];
  loading?: boolean;
  errorMessage?: string | null;
  /**
   * Fires the first time the panel is opened (useful for lazy-fetching
   * /api/debug/whoami per DESIGN.md §7.4).
   */
  onOpen?: () => void;
  /** Header label; defaults to "Show auth debug". */
  label?: string;
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function DebugPanel(props: DebugPanelProps): JSX.Element {
  const { sections = [], loading = false, errorMessage = null, onOpen, label } = props;
  const [opened, setOpened] = useState<boolean>(false);
  const hasFiredOpen = useRef<boolean>(false);

  const handleToggle = useCallback(
    (e: React.SyntheticEvent<HTMLDetailsElement>) => {
      const isOpen = (e.currentTarget as HTMLDetailsElement).open;
      setOpened(isOpen);
    },
    [],
  );

  useEffect(() => {
    if (opened && !hasFiredOpen.current) {
      hasFiredOpen.current = true;
      if (onOpen) onOpen();
    }
  }, [opened, onOpen]);

  return (
    <details className="rb-debug-panel" onToggle={handleToggle}>
      <summary className="rb-debug-panel__summary">
        <span className="rb-debug-panel__caret" aria-hidden="true">
          ▸
        </span>
        <span className="rb-debug-panel__label">{label ?? "Show auth debug"}</span>
        <span className="rb-debug-panel__caption">
          POC only — disabled in production
        </span>
      </summary>
      <div className="rb-debug-panel__body">
        {loading && (
          <div className="rb-debug-panel__loading">Loading debug data…</div>
        )}
        {errorMessage !== null && (
          <div className="rb-debug-panel__error" role="alert">
            {errorMessage}
          </div>
        )}
        {!loading &&
          errorMessage === null &&
          sections.map((section) => (
            <section key={section.title} className="rb-debug-panel__section">
              <h3 className="rb-debug-panel__section-title">{section.title}</h3>
              <table className="rb-debug-panel__kv">
                <tbody>
                  {section.entries.map(([k, v]) => (
                    <tr key={k}>
                      <th scope="row" className="rb-debug-panel__key">
                        {k}
                      </th>
                      <td className="rb-debug-panel__value">
                        <code>{renderValue(v)}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
      </div>
    </details>
  );
}
